/**
 * Sentry 错误监控工具
 */

import * as Sentry from '@sentry/vue'

/**
 * 手动捕获异常
 * @param error 错误对象
 * @param context 上下文信息
 */
export function captureError(
  error: Error,
  context?: Record<string, any>
): void {
  if (context) {
    Sentry.withScope(scope => {
      scope.setContext('additional', context)
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

export const ___captureError = captureError

/**
 * 捕获消息
 * @param message 消息内容
 * @param level 日志级别
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info'
): void {
  Sentry.captureMessage(message, level)
}

/**
 * 设置用户信息
 * @param user 用户信息
 */
export function setUser(user: {
  id?: string
  email?: string
  username?: string
  code?: string
}): void {
  Sentry.setUser(user)
}

/**
 * 设置标签
 * @param tags 标签对象
 */
export function setTags(tags: Record<string, string>): void {
  Sentry.setTags(tags)
}

/**
 * 添加面包屑
 * @param breadcrumb 面包屑信息
 */
export function addBreadcrumb(breadcrumb: {
  message: string
  category?: string
  level?: Sentry.SeverityLevel
  data?: Record<string, any>
}): void {
  if (import.meta.env.DEV) {
    const { message, category, level, data } = breadcrumb
    const parts = [
      '[Breadcrumb]',
      category ? `[${category}]` : '',
      level ? `[${level}]` : '',
      message
    ].filter(Boolean)

    if (data) {
      console.log(parts.join(' '), data)
    } else {
      console.log(parts.join(' '))
    }

    return
  }

  Sentry.addBreadcrumb(breadcrumb)
}

/**
 * 开始性能监控事务
 * @param name 事务名称
 * @param op 操作类型
 * @returns 事务对象
 */
export function startTransaction(name: string, op = 'navigation') {
  // 注意：不同版本的 Sentry 可能需要不同的 API
  // 这里仅提供接口，具体实现需要根据实际 Sentry 版本调整
  console.log(`Starting transaction: ${name} (${op})`)
  return null
}

/**
 * 捕获性能指标
 * @param name 指标名称
 * @param value 指标值
 * @param unit 单位
 */
export function captureMetric(
  name: string,
  value: number,
  unit = 'none'
): void {
  // 注意：不同版本的 Sentry 可能需要不同的 API
  // 这里仅提供接口，具体实现需要根据实际 Sentry 版本调整
  console.log(`Capturing metric: ${name} = ${value} ${unit}`)
}

export default Sentry
